import { useCallback, useMemo, useState } from 'react';
import PeopleIcon from '@material-ui/icons/People';
import CarIcon from '@material-ui/icons/DirectionsCar';
import SmileIcon from '@material-ui/icons/Mood';
import ClockIcon from '@material-ui/icons/Timer';
import { usePortalQuery } from '../usePortalQuery';
import { usePortalAccess } from '../usePortalAccess';
import { useFiltersByKey } from '../useFilterByKey/useFilterByKey';
import { getActiveDrivers } from '../../services/DeliveryOperationsPortalService';
import history from '../../utils/history';

export const pushToDriver = (id) => () => history.push(`/drivers/${id}`);
const EMPTY = [];

const filterOptions = [
  {
    id: 'all',
    title: 'All Drivers',
    icon: PeopleIcon,
  },
  {
    id: 'busy',
    title: 'Busy',
    icon: CarIcon,
  },
  {
    id: 'free',
    title: 'Free',
    icon: SmileIcon,
  },
  {
    id: 'soon',
    title: 'Soon',
    icon: ClockIcon,
  },
];

export const buildDriverMarkers = (driverList) =>
  (driverList || []).map((driver) => ({
    id: driver.id,
    markerType: 'driver',
    status: driver.status,
    text: `${driver?.firstName} ${driver?.lastName}`,
    latitude: driver?.location?.latitude,
    longitude: driver?.location?.longitude,
    onClick: pushToDriver(driver.id),
  }));

export const useActiveDriversRequest = () => {
  const { dispatcher, sessionVersion } = usePortalAccess();

  const {
    data: driverList,
    isFetching: driverListLoading,
    refetch,
    error,
  } = usePortalQuery(['active-drivers', sessionVersion], getActiveDrivers, {
    enabled: dispatcher,
    interval: 30000,
  });

  const refetchAndEnable = useCallback(() => {
    if (!dispatcher) return Promise.resolve([]);
    return refetch();
  }, [dispatcher, refetch]);

  return {
    driverList: dispatcher && !error ? driverList : EMPTY,
    driverListLoading,
    refetch: refetchAndEnable,
  };
};

export const useActiveDrivers = ({ driverList, refetch }) => {
  const {
    data: filteredDriverList,
    onChange: onFilterChange,
    filters,
  } = useFiltersByKey(driverList, { status: filterOptions[0].id });

  const [search, setSearch] = useState({ key: '', text: '' });

  const onSearch = (payload) => {
    setSearch({
      key: payload.key,
      text: payload.text,
    });
  };

  const cache = useMemo(() => (filteredDriverList || []).filter((value) =>
    `${value.firstName || ''} ${value.lastName || ''}`.toLowerCase().includes(search.text.toLowerCase())
  ), [filteredDriverList, search.text]);
  const driverMarkers = useMemo(() => buildDriverMarkers(cache), [cache]);

  return {
    filteredDriverList: cache,
    filterOptions,
    onFilterChange,
    filters,
    refetch,
    driverMarkers,
    onSearch,
  };
};
